import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga';
import { useHistory } from 'react-router-dom';

import Button from '../../../components/Button';
import { Theme } from '../../../components/Button/styles';
import {
  Step,
  useCheckoutGtm,
} from '../../../hooks/gtm/checkout/useCheckoutGtm';
import {
  Order,
  OrderStatus,
} from '../../../requests/caipirinha-api/interfaces/order.interface';
import { getFingerprint } from '../../../utils/fingerprint.util';
import {
  getPaymentPage,
  isBoleto,
  isPix,
} from '../../../utils/paymentType.utils';
import { getSnowdenURL } from '../../../utils/snowden.util';
import ButtonServiceAcceptance from './button-service-acceptance';
import { isContainsServices } from '../../../utils/isContainsService';

interface Props {
  order: Order;
  generatePaymentForm: (
    id: string,
    document: string,
    paymentId: string,
    fingerprint?: string,
  ) => Promise<void>;
}

export interface ButtonProps {
  theme: Theme;
  text: React.ReactNode;
  onClick: () => void | Promise<void>;
}

const ButtonContinue: React.FC<Props> = ({ order, generatePaymentForm }) => {
  const { id, details, status } = order;
  const {
    payments,
    customer: { documentNumber },
  } = details;

  const { replace } = useHistory();
  const { sendDataToGtm } = useCheckoutGtm();

  const goToSnowden = () => {
    sendDataToGtm(
      order,
      { type: 'checkout', name: '/pagamento/infos-pagamento' },
      Step.Payment,
    );
    ReactGA.outboundLink({ label: 'Resumo do pedido' }, () => {
      window.location.href = getSnowdenURL(id);
    });
  };

  const [button, setButton] = useState<ButtonProps>({
    theme: 'NORMAL',
    text: 'Continuar',
    onClick: goToSnowden,
  });

  const getPaymentId = () =>
    payments.length > 0 ? String(payments[0].id) : '';

  const goToPage = () => {
    const page = getPaymentPage(getPaymentId());
    replace(`/${id}/${documentNumber}/${page}`);
  };

  const generateGoToPage = async () => {
    await generatePaymentForm(
      id,
      documentNumber,
      getPaymentId(),
      getFingerprint(),
    );
  };

  const setButtonBoleto = () => {
    switch (status) {
      case OrderStatus.AWAITING_PAYMENT:
      case OrderStatus.DENIED:
        setButton({
          theme: 'SUCCESS',
          text: 'Gerar Boleto',
          onClick: generateGoToPage,
        });
        break;
      case OrderStatus.AUTHORIZED:
        setButton({
          theme: 'NORMAL',
          text: 'Visualizar Boleto',
          onClick: goToPage,
        });
        break;
      default:
        break;
    }
  };

  const setButtonPix = () => {
    const theme = 'SUCCESS';
    const text = 'Seguir para código Pix';
    switch (status) {
      case OrderStatus.AWAITING_PAYMENT:
      case OrderStatus.DENIED:
        setButton({
          theme,
          text,
          onClick: generateGoToPage,
        });
        break;
      case OrderStatus.AUTHORIZED:
        setButton({
          theme,
          text,
          onClick: goToPage,
        });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const paymentId = getPaymentId();
    if (isBoleto(paymentId)) {
      setButtonBoleto();
    } else if (isPix(paymentId)) {
      setButtonPix();
    }
    // eslint-disable-next-line
  }, [payments]);
  return isContainsServices(order) ? (
    <ButtonServiceAcceptance
      onClick={button.onClick}
      theme={'NORMAL'}
      text={'Aceitar e continuar'}
    />
  ) : (
    <Button
      data-cy="continue-button"
      onClick={button.onClick}
      theme={button.theme}
    >
      {button.text}
    </Button>
  );
};

export default ButtonContinue;
